<template>
  <div class="return-reasons-container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h2 class="page-title">
            <i class="fas fa-undo-alt title-icon" />
            Gestion des motifs de retour
          </h2>
          <p class="page-subtitle">
            Configurez et gérez les différents motifs de retour disponibles
          </p>
        </div>
        <b-button
          variant="primary"
          size="lg"
          class="add-button"
          @click="showAddModal = true"
        >
          <i class="fas fa-plus mr-2" />
          Ajouter un motif
        </b-button>
      </div>
    </div>

    <!-- Enhanced Top Section: Stats, Search, Filters, Results Summary -->
    <div class="enhanced-top-section">
      <div class="enhanced-stats-filters">
        <!-- Stats Cards -->
        <div class="enhanced-stats">
          <div class="enhanced-stat-card total">
            <div class="icon">
              <i class="fas fa-list" /></div>
            <div class="info">
              <div class="number">
                {{ stats.total }}
              </div>
              <div class="label">
                Total
              </div>
            </div>
          </div>
          <div class="enhanced-stat-card active">
            <div class="icon">
              <i class="fas fa-check-circle" /></div>
            <div class="info">
              <div class="number">
                {{ stats.active }}
              </div>
              <div class="label">
                Actifs
              </div>
            </div>
          </div>
          <div class="enhanced-stat-card inactive">
            <div class="icon">
              <i class="fas fa-times-circle" /></div>
            <div class="info">
              <div class="number">
                {{ stats.inactive }}
              </div>
              <div class="label">
                Inactifs
              </div>
            </div>
          </div>
        </div>

        <!-- Search and Filters -->
        <div class="enhanced-filters">
          <div class="enhanced-search">
            <i class="fas fa-search search-icon" />
            <b-form-input
              v-model="searchQuery"
              placeholder="Rechercher un motif..."
              class="search-input"
            />
            <b-button
              v-if="searchQuery"
              variant="link"
              class="clear-search"
              @click="searchQuery = ''"
            >
              <i class="fas fa-times" />
            </b-button>
          </div>
          <div class="enhanced-status-filters">
            <span class="filter-label">Statut :</span>
            <b-button-group class="status-filters">
              <b-button
                :variant="statusFilter === 'active' ? 'success' : 'outline-success'"
                :class="{ active: statusFilter === 'active' }"
                @click="statusFilter = 'active'"
              >
                <i class="fas fa-check-circle mr-1" />
                Actifs ({{ activeReasonsCount }})
              </b-button>
              <b-button
                :variant="statusFilter === 'inactive' ? 'danger' : 'outline-danger'"
                :class="{ active: statusFilter === 'inactive' }"
                @click="statusFilter = 'inactive'"
              >
                <i class="fas fa-pause-circle mr-1" />
                Inactifs ({{ inactiveReasonsCount }})
              </b-button>
            </b-button-group>
          </div>
        </div>
      </div>
      <!-- Results Summary -->
      <div
        v-if="filteredReasons.length > 0"
        class="enhanced-results-summary"
      >
        <span class="results-count">
          {{ filteredReasons.length }} motif{{ filteredReasons.length > 1 ? 's' : '' }} trouvé{{ filteredReasons.length > 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <!-- Table Card -->
    <b-card class="table-card">
      <div
        v-if="filteredReasons.length > 0"
        class="bulk-actions-bar"
      >
        <div class="bulk-selection-summary">
          <b-form-checkbox
            :checked="allFilteredSelected"
            :indeterminate="selectedReasonCount > 0 && !allFilteredSelected"
            @change="toggleAllFiltered"
          >
            Sélectionner les motifs affichés
          </b-form-checkbox>
          <span
            v-if="selectedReasonCount"
            class="selected-count"
          >
            {{ selectedReasonCount }} sélectionné{{ selectedReasonCount > 1 ? 's' : '' }}
          </span>
        </div>
        <div class="bulk-action-buttons">
          <b-button
            size="sm"
            variant="outline-success"
            :disabled="!selectedReasonCount"
            @click="setMobileVisibilityForSelected(true)"
          >
            <i class="fas fa-mobile-alt mr-1" />
            Visible mobile
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="!selectedReasonCount"
            @click="setMobileVisibilityForSelected(false)"
          >
            <i class="fas fa-eye-slash mr-1" />
            Masquer mobile
          </b-button>
          <b-button
            v-if="selectedReasonCount"
            size="sm"
            variant="link"
            class="clear-selection-btn"
            @click="clearSelection"
          >
            Réinitialiser
          </b-button>
        </div>
      </div>

      <div
        v-if="filteredReasons.length === 0"
        class="empty-state"
      >
        <div class="empty-icon">
          <i class="fas fa-search" />
        </div>
        <h4>Aucun motif trouvé</h4>
        <p v-if="searchQuery">
          Aucun motif ne correspond à votre recherche "{{ searchQuery }}"
        </p>
        <p v-else-if="statusFilter !== 'all'">
          Aucun motif {{ statusFilter === 'active' ? 'actif' : 'inactif' }} trouvé
        </p>
        <p v-else>
          Commencez par ajouter votre premier motif de retour
        </p>
        <b-button
          v-if="!searchQuery && statusFilter === 'all'"
          variant="primary"
          @click="showAddModal = true"
        >
          <i class="fas fa-plus mr-2" />
          Ajouter le premier motif
        </b-button>
      </div>

      <div
        v-else
        class="table-responsive"
      >
        <b-table
          :items="filteredReasons"
          :fields="enhancedFields"
          hover
          class="modern-table"
          :tbody-tr-class="getRowClass"
        >
          <template #cell(select)="{ item }">
            <b-form-checkbox
              :checked="isReasonSelected(item.id)"
              @change="checked => toggleReasonSelection(item.id, checked)"
            />
          </template>

          <template #cell(content)="{ item }">
            <div class="reason-content">
              <strong>{{ item.content }}</strong>
              <div class="reason-features">
                <b-badge
                  v-if="item.require_planification"
                  v-b-tooltip.hover
                  variant="info"
                  class="feature-badge"
                  title="Planification requise"
                >
                  <i class="fas fa-calendar-alt mr-1" />Plan.
                </b-badge>
                <b-badge
                  v-if="item.require_datetime"
                  v-b-tooltip.hover
                  variant="warning"
                  class="feature-badge"
                  title="Date/Heure requise"
                >
                  <i class="fas fa-clock mr-1" />Date
                </b-badge>
                <b-badge
                  v-if="item.require_additional_note"
                  v-b-tooltip.hover
                  variant="secondary"
                  class="feature-badge"
                  title="Note supplémentaire requise"
                >
                  <i class="fas fa-sticky-note mr-1" />Note
                </b-badge>
              </div>
            </div>
          </template>

          <template #cell(is_active)="{ item }">
            <b-badge
              :variant="item.is_active ? 'success' : 'secondary'"
              class="status-badge"
            >
              <i
                :class="item.is_active ? 'fas fa-check-circle' : 'fas fa-pause-circle'"
                class="mr-1"
              />
              {{ item.is_active ? 'Actif' : 'Inactif' }}
            </b-badge>
          </template>

          <template #cell(visible_on_mobile_app)="{ item }">
            <div class="mobile-visibility-cell">
              <b-badge
                :variant="item.visible_on_mobile_app ? 'success' : 'secondary'"
                class="status-badge"
              >
                <i
                  :class="item.visible_on_mobile_app ? 'fas fa-mobile-alt' : 'fas fa-eye-slash'"
                  class="mr-1"
                />
                {{ item.visible_on_mobile_app ? 'Visible' : 'Masqué' }}
              </b-badge>
              <b-button
                v-b-tooltip.hover
                size="sm"
                variant="link"
                class="mobile-toggle-btn"
                :title="item.visible_on_mobile_app ? 'Masquer dans mobile app' : 'Afficher dans mobile app'"
                @click="setMobileVisibility([item.id], !item.visible_on_mobile_app)"
              >
                {{ item.visible_on_mobile_app ? 'Masquer' : 'Afficher' }}
              </b-button>
            </div>
          </template>

          <template #cell(placeholders)="{ item }">
            <div class="placeholders-cell">
              <div
                v-if="item.datetime_placeholder"
                class="placeholder-item"
              >
                <small class="placeholder-label">Date/Heure:</small>
                <span class="placeholder-text">{{ item.datetime_placeholder }}</span>
              </div>
              <div
                v-if="item.additional_note_placeholder"
                class="placeholder-item"
              >
                <small class="placeholder-label">Note:</small>
                <span class="placeholder-text">{{ item.additional_note_placeholder }}</span>
              </div>
              <span
                v-if="!item.datetime_placeholder && !item.additional_note_placeholder"
                class="text-muted"
              >
                Aucun placeholder
              </span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="action-buttons">
              <b-button
                v-b-tooltip.hover
                :variant="item.is_active ? 'outline-warning' : 'outline-success'"
                size="sm"
                class="action-btn toggle-btn"
                :title="item.is_active ? 'Désactiver ce motif' : 'Activer ce motif'"
                @click="toggleStatus(item)"
              >
                <i :class="item.is_active ? 'fas fa-pause' : 'fas fa-play'" />
                <span class="btn-text">{{ item.is_active ? 'Désactiver' : 'Activer' }}</span>
              </b-button>
              <b-button
                v-b-tooltip.hover
                size="sm"
                variant="outline-primary"
                class="action-btn edit-btn"
                title="Éditer ce motif"
                @click="editReason(item)"
              >
                <i class="fas fa-edit" />
                <span class="btn-text">Éditer</span>
              </b-button>
              <b-button
                v-b-tooltip.hover
                size="sm"
                variant="outline-danger"
                class="action-btn delete-btn"
                title="Supprimer ce motif"
                @click="deleteReason(item)"
              >
                <i class="fas fa-trash-alt" />
                <span class="btn-text">Supprimer</span>
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Enhanced Add/Edit Modal -->
    <b-modal
      v-model="showAddModal"
      :title="isEditing ? 'Éditer le motif' : 'Ajouter un nouveau motif'"
      size="lg"
      header-class="modal-header-custom"
      body-class="modal-body-custom"
      @hidden="resetForm"
      @ok="saveReason"
    >
      <template #modal-header="{ close }">
        <div class="modal-header-content">
          <div class="modal-title">
            <i
              :class="isEditing ? 'fas fa-edit' : 'fas fa-plus'"
              class="modal-icon"
            />
            {{ isEditing ? 'Éditer le motif' : 'Ajouter un nouveau motif' }}
          </div>
          <b-button
            variant="link"
            class="modal-close"
            @click="close()"
          >
            <i class="fas fa-times" />
          </b-button>
        </div>
      </template>

      <div class="modal-form">
        <div class="form-section">
          <h5 class="section-title">
            Informations générales
          </h5>

          <b-form-group
            label="Libellé du motif"
            label-for="reason-content"
            class="form-group-enhanced"
          >
            <b-form-input
              id="reason-content"
              v-model="editForm.content"
              placeholder="Ex: Produit défectueux, Erreur de commande..."
              required
              class="form-input-enhanced"
              :state="editForm.content ? true : null"
            />
          </b-form-group>

          <div class="status-toggle">
            <b-form-checkbox
              v-model="editForm.is_active"
              class="status-checkbox"
              size="lg"
            >
              <strong>Motif actif</strong>
              <small class="text-muted d-block">Ce motif sera disponible dans la liste de sélection</small>
            </b-form-checkbox>
          </div>

          <div class="status-toggle">
            <b-form-checkbox
              v-model="editForm.visible_on_mobile_app"
              class="status-checkbox"
              size="lg"
            >
              <strong>Visible sur mobile app</strong>
              <small class="text-muted d-block">Ce motif sera disponible pour les livreurs dans l'application mobile</small>
            </b-form-checkbox>
          </div>
        </div>

        <div class="form-section">
          <h5 class="section-title">
            Configuration des champs requis
          </h5>

          <div class="requirements-grid">
            <div class="requirement-item">
              <b-form-checkbox
                v-model="editForm.require_planification"
                class="requirement-checkbox"
              >
                <div class="requirement-content">
                  <i class="fas fa-calendar-alt requirement-icon" />
                  <div>
                    <strong>Planification requise</strong>
                    <small class="text-muted d-block">Le client devra planifier une intervention</small>
                  </div>
                </div>
              </b-form-checkbox>
            </div>

            <div class="requirement-item">
              <b-form-checkbox
                v-model="editForm.require_datetime"
                class="requirement-checkbox"
              >
                <div class="requirement-content">
                  <i class="fas fa-clock requirement-icon" />
                  <div>
                    <strong>Date/Heure requise</strong>
                    <small class="text-muted d-block">Le client devra spécifier une date et heure</small>
                  </div>
                </div>
              </b-form-checkbox>
            </div>

            <div class="requirement-item">
              <b-form-checkbox
                v-model="editForm.require_additional_note"
                class="requirement-checkbox"
              >
                <div class="requirement-content">
                  <i class="fas fa-sticky-note requirement-icon" />
                  <div>
                    <strong>Note supplémentaire requise</strong>
                    <small class="text-muted d-block">Le client devra ajouter des détails</small>
                  </div>
                </div>
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h5 class="section-title">
            Textes d'aide (Placeholders)
          </h5>

          <b-form-group
            label="Placeholder Date/Heure"
            label-for="datetime-placeholder"
            class="form-group-enhanced"
          >
            <b-form-input
              id="datetime-placeholder"
              v-model="editForm.datetime_placeholder"
              placeholder="Ex: Date souhaitée par le client pour l'échange"
              class="form-input-enhanced"
              :disabled="!editForm.require_datetime"
            />
            <small class="text-muted">
              Texte d'aide affiché dans le champ date/heure
            </small>
          </b-form-group>

          <b-form-group
            label="Placeholder Note"
            label-for="note-placeholder"
            class="form-group-enhanced"
          >
            <b-form-input
              id="note-placeholder"
              v-model="editForm.additional_note_placeholder"
              placeholder="Ex: Précisez les instructions pour la livraison..."
              class="form-input-enhanced"
              :disabled="!editForm.require_additional_note"
            />
            <small class="text-muted">
              Texte d'aide affiché dans le champ note supplémentaire
            </small>
          </b-form-group>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <div class="modal-footer-custom">
          <b-button
            variant="secondary"
            @click="cancel()"
          >
            <i class="fas fa-times mr-2" />
            Annuler
          </b-button>
          <b-button
            variant="primary"
            :disabled="!editForm.content"
            class="save-btn"
            @click="ok()"
          >
            <i class="fas fa-save mr-2" />
            {{ isEditing ? 'Mettre à jour' : 'Ajouter' }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard, BButton, BButtonGroup, BFormGroup, BFormCheckbox, BFormInput,
  BBadge, BTable, BModal,
} from 'bootstrap-vue'

export default {
  name: 'ReturnReasonTypes',
  components: {
    BCard,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormCheckbox,
    BFormInput,
    BBadge,
    BTable,
    BModal,
  },
  data() {
    return {
      reasons: [],
      statusFilter: 'active',
      searchQuery: '',
      selectedReasonIds: [],
      showAddModal: false,
      stats: {
        total: 0,
        active: 0,
        inactive: 0,
      },
      editForm: {
        id: null,
        content: '',
        is_active: true,
        visible_on_mobile_app: false,
        require_planification: false,
        require_datetime: false,
        require_additional_note: false,
        datetime_placeholder: '',
        additional_note_placeholder: '',
        quick_options: [],
      },
      enhancedFields: [
        { key: 'select', label: '', class: 'select-column' },
        { key: 'content', label: 'Motif de retour', sortable: true },
        { key: 'is_active', label: 'Statut', sortable: true },
        { key: 'visible_on_mobile_app', label: 'Mobile app', sortable: true },
        { key: 'placeholders', label: 'Textes d\'aide' },
        { key: 'actions', label: 'Actions', class: 'actions-column' },
      ],
    }
  },
  computed: {
    filteredReasons() {
      let filtered = this.reasons

      // Filter by status
      if (this.statusFilter === 'active') {
        filtered = filtered.filter(r => r.is_active)
      } else if (this.statusFilter === 'inactive') {
        filtered = filtered.filter(r => !r.is_active)
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(r => r.content.toLowerCase().includes(query)
          || (r.datetime_placeholder && r.datetime_placeholder.toLowerCase().includes(query))
          || (r.additional_note_placeholder && r.additional_note_placeholder.toLowerCase().includes(query)))
      }

      return filtered
    },
    activeReasonsCount() {
      return this.reasons.filter(r => r.is_active).length
    },
    inactiveReasonsCount() {
      return this.reasons.filter(r => !r.is_active).length
    },
    isEditing() {
      return this.editForm.id !== null
    },
    selectedReasonCount() {
      return this.selectedReasonIds.length
    },
    filteredReasonIds() {
      return this.filteredReasons.map(reason => reason.id)
    },
    allFilteredSelected() {
      return this.filteredReasonIds.length > 0
        && this.filteredReasonIds.every(id => this.selectedReasonIds.includes(id))
    },
  },
  watch: {
    statusFilter() {
      this.fetchReasons()
    },
  },
  created() {
    this.fetchReasons()
    this.fetchStats()
  },
  methods: {
    async fetchReasons() {
      try {
        let params = {}
        if (this.statusFilter === 'active') {
          params = { is_active: true }
        } else if (this.statusFilter === 'inactive') {
          params = { is_active: false }
        }
        const { data } = await axios.get('/api/return-vouchers/reason-types/', { params })
        this.reasons = data.results || data
        this.selectedReasonIds = this.selectedReasonIds.filter(id => this.reasons.some(reason => reason.id === id))
        this.fetchStats()
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement des motifs', {
          title: 'Erreur',
          variant: 'danger',
        })
      }
    },
    async fetchStats() {
      try {
        const { data } = await axios.get('/api/return-vouchers/reason-types/stats/')
        this.stats = data
      } catch (error) {
        this.stats = { total: 0, active: 0, inactive: 0 }
      }
    },
    editReason(item) {
      this.editForm = {
        ...item,
        visible_on_mobile_app: item.visible_on_mobile_app !== false,
      }
      this.showAddModal = true
    },
    resetForm() {
      this.editForm = {
        id: null,
        content: '',
        is_active: true,
        visible_on_mobile_app: false,
        require_planification: false,
        require_datetime: false,
        require_additional_note: false,
        datetime_placeholder: '',
        additional_note_placeholder: '',
        quick_options: [],
      }
    },
    async saveReason() {
      try {
        if (this.editForm.id) {
          await axios.put(`/api/return-vouchers/reason-types/${this.editForm.id}/`, this.editForm)
          this.$bvToast.toast('Motif mis à jour avec succès', {
            title: 'Succès',
            variant: 'success',
          })
        } else {
          await axios.post('/api/return-vouchers/reason-types/', this.editForm)
          this.$bvToast.toast('Motif ajouté avec succès', {
            title: 'Succès',
            variant: 'success',
          })
        }
        this.showAddModal = false
        this.resetForm()
        this.fetchReasons()
      } catch (error) {
        this.$bvToast.toast('Erreur lors de la sauvegarde', {
          title: 'Erreur',
          variant: 'danger',
        })
      }
    },
    isReasonSelected(id) {
      return this.selectedReasonIds.includes(id)
    },
    toggleReasonSelection(id, checked) {
      if (checked && !this.selectedReasonIds.includes(id)) {
        this.selectedReasonIds = [...this.selectedReasonIds, id]
      } else if (!checked) {
        this.selectedReasonIds = this.selectedReasonIds.filter(item => item !== id)
      }
    },
    toggleAllFiltered(checked) {
      if (checked) {
        this.selectedReasonIds = Array.from(new Set([
          ...this.selectedReasonIds,
          ...this.filteredReasonIds,
        ]))
      } else {
        this.selectedReasonIds = this.selectedReasonIds.filter(id => !this.filteredReasonIds.includes(id))
      }
    },
    clearSelection() {
      this.selectedReasonIds = []
    },
    async setMobileVisibilityForSelected(visible) {
      await this.setMobileVisibility(this.selectedReasonIds, visible)
    },
    async setMobileVisibility(ids, visible) {
      if (!ids.length) return
      try {
        await axios.patch('/api/return-vouchers/reason-types/bulk-mobile-visibility/', {
          ids,
          visible_on_mobile_app: visible,
        })
        this.$bvToast.toast(
          `Motif${ids.length > 1 ? 's' : ''} ${visible ? 'visible' : 'masqué'}${ids.length > 1 ? 's' : ''} sur mobile app`,
          {
            title: 'Succès',
            variant: 'success',
          },
        )
        this.clearSelection()
        await this.fetchReasons()
      } catch (error) {
        this.$bvToast.toast('Erreur lors de la mise à jour mobile app', {
          title: 'Erreur',
          variant: 'danger',
        })
      }
    },
    async toggleStatus(item) {
      const action = item.is_active ? 'désactiver' : 'activer'
      this.$bvModal.msgBoxConfirm(
        `Êtes-vous sûr de vouloir ${action} le motif "${item.content}" ?`,
        {
          title: `Confirmation de ${action}`,
          size: 'md',
          okVariant: item.is_active ? 'warning' : 'success',
          okTitle: item.is_active ? 'Désactiver' : 'Activer',
          cancelTitle: 'Annuler',
          footerClass: 'p-3',
          centered: true,
        },
      ).then(async confirmed => {
        if (confirmed) {
          try {
            const updatedItem = { ...item, is_active: !item.is_active }
            await axios.put(`/api/return-vouchers/reason-types/${item.id}/`, updatedItem)
            this.fetchReasons()
            this.fetchStats()
            this.$bvToast.toast(
              `Motif ${updatedItem.is_active ? 'activé' : 'désactivé'} avec succès`,
              {
                title: 'Succès',
                variant: 'success',
              },
            )
          } catch (error) {
            this.$bvToast.toast('Erreur lors de la modification du statut', {
              title: 'Erreur',
              variant: 'danger',
            })
          }
        }
      })
    },
    async deleteReason(item) {
      this.$bvModal.msgBoxConfirm(
        `Êtes-vous sûr de vouloir supprimer le motif "${item.content}" ?`,
        {
          title: 'Confirmation de suppression',
          size: 'md',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
          footerClass: 'p-3',
          centered: true,
        },
      ).then(async confirmed => {
        if (confirmed) {
          try {
            await axios.delete(`/api/return-vouchers/reason-types/${item.id}/`)
            this.fetchReasons()
            this.$bvToast.toast('Motif supprimé avec succès', {
              title: 'Succès',
              variant: 'success',
            })
          } catch (err) {
            const message = err.response && err.response.status === 409
              ? 'Ce motif est utilisé et ne peut pas être supprimé.'
              : 'Erreur lors de la suppression du motif.'
            this.$bvToast.toast(message, {
              title: 'Erreur',
              variant: 'danger',
            })
          }
        }
      })
    },
    getRowClass(item) {
      return item.is_active ? 'active-row' : 'inactive-row'
    },
  },
}
</script>

<style scoped>
.return-reasons-container {
  padding: 1.5rem;
  background: #f8f9fa;
  min-height: 100vh;
}

/* Header Styles */
.page-header {
  background: linear-gradient(90deg, #f7fafc 0%, #e3e8ee 100%);
  border-radius: 12px;
  padding: 2rem;
  color: #1a202c;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(227, 6, 19, 0.08);
  border: 1.5px solid #e3e8ee;
}
.page-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1a202c;
}
.title-icon {
  font-size: 1.75rem;
  opacity: 0.9;
  color: #ED1919;
}
.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
  color: #4a5568;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  font-size: 1.75rem;
  opacity: 0.9;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
}

.add-button {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  font-weight: 500;
}

.add-button:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

/* Stats Section */
.stats-section {
  margin-bottom: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
}

.stat-total .stat-icon {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stat-active .stat-icon {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.stat-inactive .stat-icon {
  background: linear-gradient(135deg, #6c757d, #495057);
}

.stat-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #495057;
}

.stat-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

/* Filters Card */
.filters-card {
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.filters-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.search-section {
  flex: 1;
}

.search-input-wrapper {
  position: relative;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  padding-left: 40px;
  padding-right: 40px;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  padding: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-label {
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.status-filters {
  border-radius: 8px;
}

.status-filters .btn {
  border-radius: 0;
  border-width: 2px;
  font-weight: 500;
}

.status-filters .btn:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.status-filters .btn:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

/* Results Summary */
.results-summary {
  margin-bottom: 1rem;
}

.results-count {
  color: #6c757d;
  font-weight: 500;
}

/* Table Card */
.table-card {
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
}

.modern-table {
  margin: 0;
}

.modern-table thead th {
  background: #f8f9fa;
  border: none;
  font-weight: 600;
  color: #495057;
  padding: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.modern-table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-top: 1px solid #f1f3f4;
}

.modern-table tbody tr:hover {
  background: #f8f9fa;
}

.active-row {
  background: rgba(40, 167, 69, 0.02);
}

.inactive-row {
  background: rgba(108, 117, 125, 0.02);
}

/* Table Cell Styles */
.reason-content strong {
  color: #495057;
  font-weight: 600;
}

.reason-features {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.feature-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
}

.status-badge {
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
}

.placeholders-cell {
  max-width: 200px;
}

.placeholder-item {
  margin-bottom: 0.5rem;
}

.placeholder-item:last-child {
  margin-bottom: 0;
}

.placeholder-label {
  font-weight: 600;
  color: #6c757d;
  display: block;
}

.placeholder-text {
  color: #495057;
  font-size: 0.9rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  border-radius: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.toggle-btn:hover {
  transform: translateY(-1px);
}

.toggle-btn.btn-outline-warning:hover {
  background: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.toggle-btn.btn-outline-success:hover {
  background: #28a745;
  border-color: #28a745;
  color: white;
}

.edit-btn:hover {
  background: #007bff;
  border-color: #007bff;
  color: white;
}

.delete-btn:hover {
  background: #dc3545;
  border-color: #dc3545;
  color: white;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h4 {
  color: #495057;
  margin-bottom: 1rem;
}

.empty-state p {
  margin-bottom: 1.5rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

/* Modal Styles */
.modal-header-custom {
  border: none;
  padding: 2rem 3rem 1.5rem 3rem;
  background: linear-gradient(135deg, #ED1919 0%, #222 100%); /* MZ Logistic branding */
  border-radius: 0.5rem 0.5rem 0 0;
}

.modal-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  color: white;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-icon {
  font-size: 1.125rem;
}

.modal-close {
  color: white;
  opacity: 0.8;
  transition: opacity 0.3s ease;
  padding: 0.5rem;
  border-radius: 50%;
}

.modal-close:hover {
  opacity: 1;
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.modal-body-custom {
  padding: 2.5rem 3rem;
}

.modal-form {
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e9ecef;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-group-enhanced {
  margin-bottom: 1.5rem;
}

.form-input-enhanced {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  transition: all 0.3s ease;
}

.form-input-enhanced:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.form-input-enhanced:disabled {
  background: #f8f9fa;
  opacity: 0.6;
}

.status-toggle {
  background: #fff6f6;
  padding: 1.25rem 1.5rem;
  border-radius: 8px;
  border: 2px solid #ED1919;
  border-left: 6px solid #ED1919;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 6px rgba(227, 6, 19, 0.06);
}

.status-checkbox {
  font-size: 1.15rem;
  font-weight: 700;
  color: #ED1919;
}

.status-checkbox strong {
  color: #ED1919;
  font-weight: 700;
}

.status-checkbox small {
  color: #888;
  font-weight: 400;
}

.requirements-grid {
  display: grid;
  gap: 1rem;
}

.requirement-item {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  transition: border-color 0.3s ease;
}

.requirement-item:hover {
  border-color: #c2cff0;
}

.requirement-checkbox {
  margin: 0;
}

.requirement-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding-left: 0.25rem;
}

.requirement-icon {
  color: #667eea;
  font-size: 1.125rem;
  margin-top: 0.125rem;
}

.modal-footer-custom {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e9ecef;
}

.save-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: transform 0.3s ease;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  background: linear-gradient(135deg, #5a6fd8, #6a42a0);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .filters-container {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-section {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .return-reasons-container {
    padding: 1rem;
  }

  .page-header {
    padding: 1.5rem;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .page-title {
    font-size: 1.5rem;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .status-filters {
    flex-direction: column;
    width: 100%;
  }

  .status-filters .btn {
    border-radius: 8px !important;
    margin-bottom: 0.5rem;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-btn .btn-text {
    display: inline;
  }

  .requirements-grid {
    grid-template-columns: 1fr;
  }

  .modal-body-custom {
    padding: 1.5rem;
  }

  .modal-footer-custom {
    padding: 1rem 1.5rem;
    flex-direction: column-reverse;
    gap: 0.75rem;
  }

  .modal-footer-custom .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .search-input-wrapper {
    max-width: 100%;
  }

  .action-btn .btn-text {
    display: none;
  }

  .action-btn {
    min-width: 40px;
    justify-content: center;
  }

  .placeholders-cell {
    max-width: 150px;
  }

  .feature-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
}

/* Table Responsive Improvements */
@media (max-width: 992px) {
  .table-responsive {
    border-radius: 8px;
  }

  .modern-table {
    font-size: 0.9rem;
  }

  .modern-table thead th,
  .modern-table tbody td {
    padding: 0.75rem 0.5rem;
  }
}

/* Print Styles */
@media print {
  .page-header,
  .filters-card,
  .stats-section,
  .action-buttons {
    display: none;
  }

  .return-reasons-container {
    background: white;
    padding: 0;
  }

  .table-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}

/* Accessibility Improvements */
.btn:focus,
.form-control:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.btn:focus-visible,
.form-control:focus-visible {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

/* Animation Classes */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table-card,
.stats-section,
.filters-card {
  animation: fadeIn 0.5s ease-out;
}

.bulk-actions-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0.5rem 1rem;
  border-bottom: 1px solid #eef0f4;
  margin-bottom: 0.75rem;
}

.bulk-selection-summary,
.bulk-action-buttons {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.selected-count {
  color: #0f3d91;
  font-weight: 600;
}

.clear-selection-btn {
  padding-left: 0;
  padding-right: 0;
}

.select-column {
  width: 48px;
  text-align: center;
}

.mobile-visibility-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.mobile-toggle-btn {
  padding: 0;
  font-weight: 600;
}

/* Custom Scrollbar */
.modal-form::-webkit-scrollbar {
  width: 6px;
}

.modal-form::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;

}

.modal-form::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
.modal-form::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Enhanced Top Section Styles - MZ Logistic Branding */
.enhanced-top-section {
  margin-bottom: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(220, 38, 38, 0.10); /* MZ Logistic red shadow */
  padding: 1.5rem 2rem 1rem 2rem;
}
.enhanced-stats-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
}
.enhanced-stats {
  display: flex;
  gap: 1.25rem;
}
.enhanced-stat-card {
  display: flex;
  align-items: center;
  background: #f9f6f6;
  border-radius: 10px;
  padding: 1rem 1.5rem;
  box-shadow: 0 1px 4px rgba(220,38,38,0.07); /* MZ Logistic red shadow */
  min-width: 120px;
  gap: 0.75rem;
  border: 1.5px solid #e5e7eb;
}
.enhanced-stat-card .icon {
  font-size: 1.5rem;
  color: #ED1919; /* MZ Logistic red */
  margin-right: 0.5rem;
}
.enhanced-stat-card.total .icon {
  color: #ED1919; /* MZ Logistic red */
}
.enhanced-stat-card.active .icon {
  color: #222; /* Black for contrast */
}
.enhanced-stat-card.inactive .icon {
  color: #bdbdbd; /* Light gray */
}
.enhanced-stat-card .number {
  font-size: 1.3rem;
  font-weight: 700;
  color: #ED1919; /* MZ Logistic red */
}
.enhanced-stat-card .label {
  font-size: 0.9rem;
  color: #222;
  font-weight: 500;
}
.enhanced-filters {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 260px;
}
.enhanced-search {
  display: flex;
  align-items: center;
  position: relative;
}
.enhanced-search .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #ED1919; /* MZ Logistic red */
}
.enhanced-search .search-input {
  padding-left: 38px;
  border-radius: 8px;
  border: 2px solid #ED1919;
  transition: border-color 0.3s ease;
}
.enhanced-search .search-input:focus {
  border-color: #ED1919;
}
.enhanced-search .clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #ED1919;
  padding: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}
.enhanced-status-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.enhanced-status-filters .filter-label {
  font-weight: 600;
  color: #ED1919;
  margin-right: 0.5rem;
}
.enhanced-results-summary {
  margin-top: 1rem;
  color: #ED1919;
  font-weight: 500;
  font-size: 1rem;
}

@media (max-width: 992px) {
  .enhanced-top-section {
    padding: 1rem;
  }
  .enhanced-stats-filters {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }
  .enhanced-stats {
    justify-content: flex-start;
  }
  .enhanced-filters {
    min-width: 0;
  }
}

@media (max-width: 576px) {
  .enhanced-stats {
    flex-direction: column;
    gap: 0.5rem;
  }
  .enhanced-top-section {
    padding: 0.5rem;
  }
}

</style>
